import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  output,
  viewChild,
} from '@angular/core';
import { translation } from '@cue/translations';
import {
  ConfigService,
  currentUser,
  getTextKeyAndClass,
  IdentifiedAssistEvent,
  isAllDayEvent,
  ResourceAttendee,
} from '@assist/shared/data';

import {
  cueCheckCircle,
  cueCompass,
  cueOrders,
  cueDirections,
  cueShuffle,
  cueExclamationCircle,
  cueFinish,
  cueSearch,
  cueClose,
  cueUser,
  cueMap,
  IconComponent,
} from '@cue/ui/icons';
import { ResourceTitlePipe } from '@assist/ui/pipes';

import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ImageWidthPipe, LocalizedStringPipe } from '@cue/utilities';
import { TooltipDirective, TooltipPositions } from '@cue/ui/tooltips';
import { AssistResourceFileType } from '@cue/api';
import { ButtonItemTemplateDirective, DropDownButtonComponent } from '@progress/kendo-angular-buttons';
import { ExpansionPanelComponent } from '@progress/kendo-angular-layout';
import { startOfDay } from 'date-fns';
import { NgClass, NgStyle } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { DateFormatPipe, DateTimeFormatPipe, SubjectPipe, TimeFormatPipe } from '@cue/pipes';
import { ButtonComponent } from '@cue/ui';
@Component({
  selector: 'assist-reservation-card',
  templateUrl: './reservation-card.component.html',
  styleUrls: ['./reservation-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LocalizedStringPipe,
    IconComponent,
    TranslocoPipe,
    TimeFormatPipe,
    DateFormatPipe,
    DateTimeFormatPipe,
    TooltipDirective,
    ImageWidthPipe,
    SubjectPipe,
    ButtonComponent,
    DropDownButtonComponent,
    ResourceTitlePipe,
    ButtonItemTemplateDirective,
    ExpansionPanelComponent,
    NgClass,
    NgStyle,
  ],
  providers: [TimeFormatPipe, DateFormatPipe],
  host: {
    '[class.big-confirm-button]': 'showBigConfirmButton()',
  },
})
export class ReservationCardComponent implements OnDestroy {
  private timeFormatPipe = inject(TimeFormatPipe);
  private dateFormatPipe = inject(DateFormatPipe);
  protected configService = inject(ConfigService);
  private translateService = inject(TranslocoService);
  event = input.required<IdentifiedAssistEvent>();

  showBigConfirmButton = input<boolean, boolean | 'true' | 'false'>(false, {
    transform: booleanAttribute,
  });
  showImage = input<boolean, boolean | 'true' | 'false'>(true, {
    transform: booleanAttribute,
  });

  confirmOnLabels = input<boolean, boolean | 'true' | 'false'>(false, {
    transform: booleanAttribute,
  });

  showNavigate = input<boolean, boolean | 'true' | 'false'>(true, {
    transform: booleanAttribute,
  });

  isColleague = input<boolean, boolean | string>(false, {
    transform: booleanAttribute,
  });

  store = inject(Store);
  currentUserSignal = toSignal(this.store.select(currentUser));

  onLabelConfirm(event: Event) {
    if (this.confirmOnLabels()) {
      if (this.confirmAvailable()) {
        this.onConfirm(event);
      }
    }
  }

  confirmAvailable = computed(() => {
    return this.event().showConfirmButton && !this.isColleague();
  });

  currentUserInAttendees() {
    return this.event().attendees.some(
      (x) => x.emailAddress?.address.toLowerCase() === this.currentUserSignal().data?.email.toLowerCase()
    );
  }

  detail = output<IdentifiedAssistEvent>();
  navigate = output<{
    event: IdentifiedAssistEvent;
    resourceAttendee: ResourceAttendee;
  }>();
  confirm = output<IdentifiedAssistEvent>();
  change = output<IdentifiedAssistEvent>();
  show = output<{
    event: IdentifiedAssistEvent;
    resourceAttendee: ResourceAttendee;
  }>();
  delete = output<IdentifiedAssistEvent>();
  deleteSeries = output<IdentifiedAssistEvent>();
  cancelParticipation = output<IdentifiedAssistEvent>();
  finish = output<IdentifiedAssistEvent>();
  translation = translation;

  ICONS = {
    cueCheckCircle,
    cueCompass,
    cueOrders,
    cueDirections,
    cueShuffle,
    cueExclamationCircle,
    cueFinish,
    cueSearch,
    cueClose,
    cueUser,
    cueMap,
  };

  dropdown = viewChild(DropDownButtonComponent);
  now = new Date();
  totalOrdersItemCount = computed(() => {
    return this.event()?.resourceAttendees.reduce((acc, it) => acc + it.orderItemsCount, 0);
  });

  isAllDayEvent = computed(() => {
    return isAllDayEvent(this.event().start.utcDateTime, this.event().end.utcDateTime);
  });

  isRepetitiveEvent = computed(() => {
    return this.event().type !== 'singleInstance';
  });

  isOccurrenceLikeEvent = computed(() => {
    return this.event().type === 'occurrence' || this.event().type === 'exception';
  });

  isSeriesMasterEvent = computed(() => {
    return this.event().type === 'seriesMaster';
  });

  repetitiveEventTypeLabel = computed(() => {
    switch (this.event().type) {
      case 'seriesMaster':
        return this.translateService.translate(translation.reservationPopup.recurrenceTypeSeries);
      case 'occurrence':
        return this.translateService.translate(translation.reservationPopup.recurrenceTypeOccurrence);
      case 'exception':
        return this.translateService.translate(translation.reservationPopup.recurrenceTypeException);
      default:
        return '';
    }
  });

  onFinish(e: Event) {
    this.finish.emit(this.event());
    e.stopPropagation();
  }

  onShow($event: Event, resourceAttendee: ResourceAttendee) {
    if (!this.showNavigate()) return;
    this.show.emit({
      event: this.event(),
      resourceAttendee: resourceAttendee,
    });
    $event.stopPropagation();
  }

  confirmFrom(resourceAttendee: ResourceAttendee) {
    return (
      this.dateFormatPipe.transform(resourceAttendee.confirmationMetadata.lowerBorderTime) +
      ' ' +
      this.timeFormatPipe.transform(resourceAttendee.confirmationMetadata.lowerBorderTime)
    );
  }

  confirmTo(resourceAttendee: ResourceAttendee) {
    return (
      this.dateFormatPipe.transform(resourceAttendee.confirmationMetadata.higherBorderTime) +
      ' ' +
      this.timeFormatPipe.transform(resourceAttendee.confirmationMetadata.higherBorderTime)
    );
  }

  onChange(e: Event) {
    this.change.emit(this.event());
    e.stopPropagation();
  }

  onConfirm(e: Event) {
    this.confirm.emit(this.event());
    e.stopPropagation();
  }

  onDetail(e: Event) {
    if (!this.isColleague() || this.event().canViewFromColleague) {
      this.detail.emit(this.event());
    }
    e.stopPropagation();
  }

  position = TooltipPositions.LEFT;

  onDelete(e: Event) {
    this.delete.emit(this.event());
    e.stopPropagation();
  }

  onDeleteSeries(e: Event) {
    this.deleteSeries.emit(this.event());
    e.stopPropagation();
  }

  onNavigate(e: Event, resourceAttendee: ResourceAttendee) {
    if (!this.isColleague()) {
      this.navigate.emit({
        event: this.event(),
        resourceAttendee: resourceAttendee,
      });
    }
    e.stopPropagation();
  }

  onCancelParticipation(e: MouseEvent) {
    this.cancelParticipation.emit(this.event());
    e.stopPropagation();
  }

  copyToClipboard(address: string | undefined, $event: MouseEvent, element: HTMLElement) {
    if (address) {
      navigator.clipboard.writeText(address);
      document
        .getElementsByClassName('k-tooltip-content')[0]
        .getElementsByClassName('mail')[0]
        .getElementsByTagName('a')[0].innerHTML = this.translateService.translate(
        this.translation.reservationPopup.copied
      );
    }
    $event.stopPropagation();
  }

  protected readonly AssistResourceFileType = AssistResourceFileType;

  navigateableResourceAttendees = computed(() => {
    return this.event().resourceAttendees.filter((x) => x.resource.navigationEnabled);
  });

  onItemNavigate($event: ResourceAttendee) {
    this.navigate.emit({
      event: this.event(),
      resourceAttendee: $event,
    });
  }

  getTextKeyForConfirmation(resourceAttendee: ResourceAttendee) {
    if (resourceAttendee.resource.confirmationSettings.requiresConfirmation) {
      return translation.reservations.confirmationRequiredBetween;
    }
    if (
      resourceAttendee.resource.confirmationSettings.requiresConfirmationOnPanel &&
      resourceAttendee.resource.confirmationSettings.requiresConfirmationBySensor
    ) {
      return translation.reservations.confirmationSensorOrPanelRequiredBetween;
    }
    return resourceAttendee.resource.confirmationSettings.requiresConfirmationOnPanel
      ? translation.reservations.confirmationPanelRequiredBetween
      : translation.reservations.confirmationSensorRequiredBetween;
  }

  translocoService = inject(TranslocoService);
  dateTimeFormatPipe = inject(DateTimeFormatPipe);

  getExpanderTexts(resourceAttendee: ResourceAttendee, resourceTitle: string) {
    const startDayOfNow = startOfDay(this.now);
    const datas = resourceAttendee.statusesByDay.filter((day) => {
      const startOfDayDay = startOfDay(new Date(day.date));
      return startOfDayDay <= startDayOfNow;
    });

    return datas.map((data) => {
      if (data.confirmed) {
        const text = this.translocoService.translate(translation.reservations.confirmed, {
          when: this.dateTimeFormatPipe.transform(resourceAttendee.confirmedWhen),
          who: resourceAttendee.confirmedBy,
        });
        return this.dateFormatPipe.transform(new Date(data.date)) + ': ' + text;
      } else {
        const text = this.translocoService.translate(translation.reservations.unconfirmed);
        return this.dateFormatPipe.transform(new Date(data.date)) + ': ' + text;
      }
    });
  }

  getExpandeTitle(resourceAttendee: ResourceAttendee, resourceTitle: string) {
    const afterEnd = this.event().afterEnd;
    const beforeStart = this.event().start.utcDateTime > this.now;
    const allDaysConfirmed = resourceAttendee.statusesByDay.every((x) => x.confirmed);
    const neverConfirmed = resourceAttendee.statusesByDay.every((x) => !x.confirmed);
    if (afterEnd) {
      if (allDaysConfirmed) {
        return resourceTitle + ' ' + this.translocoService.translate(translation.reservations.confirmedEveryDay);
      }

      if (neverConfirmed) {
        return resourceTitle + ' ' + this.translocoService.translate(translation.reservations.confirmedNever);
      }
      return resourceTitle + ' ' + this.translocoService.translate(translation.reservations.confirmedSomeDay);
    }

    if (beforeStart) {
      return '';
    }

    if (resourceAttendee.confirmAvailable) {
      return (
        resourceTitle +
        ' ' +
        this.translocoService.translate(translation.reservations.confirmationRequiredBetween, {
          until: this.confirmTo(resourceAttendee),
          resourceName: resourceTitle,
        })
      );
    } else if (resourceAttendee.status === 'need-confirmation') {
      return (
        resourceTitle +
        ' ' +
        this.translocoService.translate(translation.reservations.confirmationRequiredBetween, {
          from: this.confirmFrom(resourceAttendee),
          to: this.confirmTo(resourceAttendee),
          resourceName: resourceTitle,
        })
      );
    }

    // "Tak nic"
    return resourceTitle;
  }

  protected readonly getTextKeyAndClass = getTextKeyAndClass;

  ngOnDestroy(): void {
    if (this.dropdown()?.open) {
      this.dropdown().toggle(false);
    }
  }

  onImageError(event: Event) {
    const target = event.target as HTMLImageElement;
    target.src = '/assets/images/placeholder.jpg';
  }
}
